/**
 * ⭐⭐ **弹窗栈**（2026-10-09 · 嵌套弹窗 `Esc` 缺陷修复）。
 *
 * 缺陷（**既有缺陷，非本批引入**）：`Modal` 把 `keydown` 挂在 **`document` 的 capture 阶段**，
 * 而 Escape 里只调了 `event.stopPropagation()` —— **同节点上的多个监听器拦不住**
 * （那需要 `stopImmediatePropagation`）⇒ **外层弹窗先注册、先执行** ⇒ 嵌套时 **`Esc` 把两层一起关掉**。
 *
 * 修法：模块级**栈**。只有**栈顶**弹窗才响应 `Esc` / 遮罩点击 ⇒
 * ① 非栈顶 ⇒ 不关闭；② 栈顶关闭 ⇒ 只少一层；③ 卸载 ⇒ **出栈**（防僵尸栈顶）。
 *
 * ⚠️ 这是**纯函数**（无 DOM / 无 React）⇒ 行为可用纯函数级测试证明。
 */

/** 栈（后进先出）；用 `symbol` 当令牌 ⇒ 无法伪造、不可比较相等性被误用。 */
const stack: symbol[] = [];

/** 打开一层弹窗：返回**令牌**（卸载时用它出栈）。 */
export function pushModal(): symbol {
  const token = Symbol("modal");
  stack.push(token);
  return token;
}

/** 卸载一层弹窗：**按令牌移除**（⛔ 不用 `pop()` —— 非栈顶卸载时会把栈顶误删）。 */
export function popModal(token: symbol): void {
  const index = stack.indexOf(token);
  if (index === -1) return;
  stack.splice(index, 1);
}

/** 是否**栈顶**（只有栈顶才允许 `Esc` / 遮罩关闭）。 */
export function isTopmostModal(token: symbol): boolean {
  return stack.length > 0 && stack[stack.length - 1] === token;
}

/** 供测试/排查：当前栈深度（⛔ 不暴露栈内容，避免被误用）。 */
export function modalStackDepth(): number {
  return stack.length;
}

/** 供测试：清空栈（⛔ 只应在测试里用）。 */
export function resetModalStackForTest(): void {
  stack.length = 0;
}
